import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../../classes/util';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter, JSONConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';

const maxLatLongDecimals = 3;
const initilizeDebounceTime = 50;

let Draw: __esri.DrawConstructor;
let Graphic: __esri.GraphicConstructor;
let CoordinateFormatter: __esri.coordinateFormatter;
let WebMercatorUtils: __esri.webMercatorUtils;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-draw-point',
    templateUrl: './draw-point.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './draw-point.component.scss']
})
export class DrawPointComponent extends BaseComponent implements OnInit {
    @Input()
    set enabled(enabled: boolean) {
        this._enabled = enabled;
        if (this._enabled) {
            this.setupHandleHiddenLayer();
            this.createPointDrawAction();
        } else {
            this.draw.reset();
            this.teardownHandleHiddenLayer();
            this.cdr.detectChanges();
        }
    }
    _enabled: boolean;

    @Output() readonly drawComplete: EventEmitter<__esri.Graphic> = new EventEmitter();
    @Output() readonly drawLayer: EventEmitter<__esri.GraphicsLayer> = new EventEmitter();

    @Input()
    symbolProperties: any;
    createLayerForEachGraphic: boolean;

    mapView: NonNullable<__esri.MapView>;
    draw: NonNullable<__esri.Draw>;
    graphicsLayer: NonNullable<__esri.GraphicsLayer>;

    @Input()
    showSettings = {
        createLayerForEachGraphic: true,
        style: true,
        size: true,
        fillColor: true,
        outlineColor: true,
        outlineWidth: true,
        coordinates: true
    };

    @Input()
    @InputConverter(JSONConverter)
    defaultSymbolProperties = {
        type: 'simple-marker', // autocasts as SimpleMarkerSymbol
        style: 'square',
        name: 'Square',
        color: 'red',
        size: 16,
        outline: { // autocasts as SimpleLineSymbol
            color: 'yellow',
            width: 3
        }
    };

    @Input()
    @InputConverter(JSONConverter)
    pointStyleOptions: IPointStyleOption[] = [
        { name: 'Circle', value: 'circle' },
        { name: 'Square', value: 'square' },
        { name: 'Cross', value: 'cross' },
        { name: 'Diamond', value: 'diamond' },
        { name: 'X', value: 'x' },
        { name: 'Triangle', value: 'triangle' },
        {
            name: 'Star',
            value: 'path',
            path: 'M436.083,190.376l-115.52,83.927l44.13,135.801c2.388,7.353-0.236,15.403-6.479,19.954   c-3.139,2.271-6.81,3.405-10.498,3.405c-3.682,0-7.364-1.135-10.479-3.405l-115.514-83.933l-115.508,83.921   c-6.266,4.552-14.733,4.552-20.986,0c-6.239-4.539-8.866-12.59-6.475-19.942l44.118-135.807L7.364,190.376   c-6.254-4.545-8.881-12.593-6.49-19.948c2.397-7.354,9.256-12.33,16.979-12.33h142.78l44.118-135.791   c2.391-7.353,9.245-12.327,16.979-12.327s14.576,4.974,16.964,12.327l44.118,135.791h142.792c7.72,0,14.576,4.977,16.976,12.33   C444.95,177.783,442.32,185.831,436.083,190.376z'
        },
        {
            name: 'Drop Pin',
            value: 'path',
            path: 'M356.208,107.051c-1.531-5.738-4.64-11.852-6.94-17.205C321.746,23.704,261.611,0,213.055,0 C148.054,0,76.463,43.586,66.905,133.427v18.355c0,0.766,0.264,7.647,0.639,11.089c5.358,42.816,39.143,88.32,64.375,131.136 c27.146,45.873,55.314,90.999,83.221,136.106c17.208-29.436,34.354-59.259,51.17-87.933c4.583-8.415,9.903-16.825,14.491-24.857 c3.058-5.348,8.9-10.696,11.569-15.672c27.145-49.699,70.838-99.782,70.838-149.104v-20.262 C363.209,126.938,356.581,108.204,356.208,107.051z M214.245,199.193c-19.107,0-40.021-9.554-50.344-35.939 c-1.538-4.2-1.414-12.617-1.414-13.388v-11.852c0-33.636,28.56-48.932,53.406-48.932c30.588,0,54.245,24.472,54.245,55.06 C270.138,174.729,244.833,199.193,214.245,199.193z'
        },
        {
            name: 'Caution',
            value: 'path',
            path: 'M29.225,23.567l-3.778-6.542c-1.139-1.972-3.002-5.2-4.141-7.172l-3.778-6.542c-1.14-1.973-3.003-1.973-4.142,0L9.609,9.853c-1.139,1.972-3.003,5.201-4.142,7.172L1.69,23.567c-1.139,1.974-0.207,3.587,2.071,3.587h23.391C29.432,27.154,30.363,25.541,29.225,23.567zM16.536,24.58h-2.241v-2.151h2.241V24.58zM16.428,20.844h-2.023l-0.201-9.204h2.407L16.428,20.844z'
        },
        {
            name: 'Flag',
            value: 'path',
            path: 'M9.5,3v10c8,0,8,4,16,4V7C17.5,7,17.5,3,9.5,3z M6.5,29h2V3h-2V29z'
        },
        {
            name: 'Car',
            value: 'path',
            path: 'M28.59,10.781h-2.242c-0.129,0-0.244,0.053-0.333,0.133c-0.716-1.143-1.457-2.058-2.032-2.633c-2-2-14-2-16,0C7.41,8.854,6.674,9.763,5.961,10.898c-0.086-0.069-0.19-0.117-0.309-0.117H3.41c-0.275,0-0.5,0.225-0.5,0.5v1.008c0,0.275,0.221,0.542,0.491,0.594l1.359,0.259c-1.174,2.619-1.866,5.877-0.778,9.14v1.938c0,0.553,0.14,1,0.313,1h2.562c0.173,0,0.313-0.447,0.313-1v-1.584c2.298,0.219,5.551,0.459,8.812,0.459c3.232,0,6.521-0.235,8.814-0.453v1.578c0,0.553,0.141,1,0.312,1h2.562c0.172,0,0.312-0.447,0.312-1l-0.002-1.938c1.087-3.261,0.397-6.516-0.775-9.134l1.392-0.265c0.271-0.052,0.491-0.318,0.491-0.594v-1.008C29.09,11.006,28.865,10.781,28.59,10.781zM7.107,18.906c-1.001,0-1.812-0.812-1.812-1.812s0.812-1.812,1.812-1.812s1.812,0.812,1.812,1.812S8.108,18.906,7.107,18.906zM5.583,13.716c0.96-2.197,2.296-3.917,3.106-4.728c0.585-0.585,3.34-1.207,7.293-1.207c3.953,0,6.708,0.622,7.293,1.207c0.811,0.811,2.146,2.53,3.106,4.728c-2.133,0.236-6.286-0.31-10.399-0.31S7.716,13.952,5.583,13.716zM24.857,18.906c-1.001,0-1.812-0.812-1.812-1.812s0.812-1.812,1.812-1.812s1.812,0.812,1.812,1.812S25.858,18.906,24.857,18.906z'
        },
        {
            name: 'Train',
            value: 'path',
            path: 'M25.781,21.382c0.69-0.691,0.607-4.311,0.607-4.311c0-2.21-0.663-10.334-1.602-12.103c-0.94-1.768-6.19-3.26-8.787-3.26s-7.847,1.492-8.786,3.26c-0.939,1.769-1.603,9.893-1.603,12.103c0,0-0.083,3.619,0.607,4.311c0.455,0.455,2.205,1.581,3.663,2.423l-3.744,6.486h2.31l0.673-1.166h13.759l0.673,1.166h2.311l-3.744-6.486C23.576,22.963,25.326,21.837,25.781,21.382zM23.819,16.795c-0.473,0-0.856-0.384-0.856-0.856s0.384-0.856,0.856-0.856c0.474,0,0.856,0.384,0.856,0.856S24.293,16.795,23.819,16.795zM20.421,6.959h3.768c0.326,1.565,0.616,3.98,0.807,6.134h-4.574V6.959zM14.417,3.125h3.166v1.333h-3.166V3.125zM7.812,6.959h3.768v6.134H7.005C7.195,10.939,7.485,8.524,7.812,6.959zM8.181,16.795c-0.474,0-0.856-0.384-0.856-0.856s0.383-0.856,0.856-0.856c0.473,0,0.856,0.384,0.856,0.856S8.653,16.795,8.181,16.795zM9.341,15.938c0-0.473,0.384-0.856,0.856-0.856c0.474,0,0.856,0.384,0.856,0.856s-0.383,0.856-0.856,0.856C9.725,16.795,9.341,16.411,9.341,15.938zM10.275,27.125l0.99-1.715h9.469l0.99,1.715H10.275zM20.946,15.938c0-0.473,0.383-0.856,0.856-0.856c0.473,0,0.856,0.384,0.856,0.856s-0.384,0.856-0.856,0.856C21.329,16.795,20.946,16.411,20.946,15.938z'
        },
        {
            name: 'Plane',
            value: 'path',
            path: 'M19.671,8.11l-2.777,2.777l-3.837-0.861c0.362-0.505,0.916-1.683,0.464-2.135c-0.518-0.517-1.979,0.278-2.305,0.604l-0.913,0.913L7.614,8.804l-2.021,2.021l2.232,1.061l-0.082,0.082l1.701,1.701l0.688-0.687l3.164,1.504L9.571,18.21H6.413l-1.137,1.138l3.6,0.948l1.83,1.83l0.947,3.598l1.137-1.137V21.43l3.725-3.725l1.504,3.164l-0.687,0.687l1.702,1.701l0.081-0.081l1.062,2.231l2.02-2.02l-0.604-2.689l0.912-0.912c0.326-0.326,1.121-1.789,0.604-2.306c-0.452-0.452-1.63,0.101-2.135,0.464l-0.861-3.838l2.777-2.777c0.947-0.947,3.599-4.862,2.62-5.839C24.533,4.512,20.618,7.163,19.671,8.11z'
        },
        {
            name: 'Parking',
            value: 'path',
            path: 'M142.5,0C63.934,0,0,63.934,0,142.5S63.934,285,142.5,285S285,221.066,285,142.5S221.066,0,142.5,0z M142.5,271.424    c-71.083,0-128.924-57.841-128.924-128.924S71.417,13.576,142.5,13.576S271.424,71.417,271.424,142.5    S213.583,271.424,142.5,271.424z M153.138,62.919H98.637c-12.586,0-20.831,4.991-20.831,20.843v120.507c0,10.632,6.928,17.807,17.788,17.807    c10.854,0,17.807-7.175,17.807-17.807v-42.336h39.515c34.524,0,54.279-21.065,54.279-49.504    C207.194,77.683,183.11,62.919,153.138,62.919z M146.632,134.583H113.4V90.269h29.545c17.584,0,28.661,6.525,28.661,22.16    C171.606,125.881,160.956,134.583,146.632,134.583z'
        },
        {
            name: 'Government',
            value: 'path',
            path: 'M76.5,213h36v230h54V246h53v197h54V246h53v197h54V213h35c5.337,0,9.67-1.5,13-4.5    c3.333-3,5-6.833,5-11.5c0-5.335-2.333-9.669-7-13v-1h-1l-170-88V69c10,4.668,25.167,5.335,45.5,2    c20.333-3.333,37.833-2.333,52.5,3V17c-14-5.333-31.5-6.333-52.5-3s-36.167,2.667-45.5-2V8c0-5.333-3-8-9-8s-9,2.667-9,8v87    l-170,88h-1v1c-5.333,3.333-8,7.667-8,13c0,4.667,1.833,8.5,5.5,11.5C67.667,211.5,71.833,213,76.5,213z'
        },
        {
            name: 'Lightning Bolt',
            value: 'path',
            path: 'M22.727,18.242L4.792,27.208l8.966-8.966l-4.483-4.484l17.933-8.966l-8.966,8.966L22.727,18.242z'
        },
        {
            name: 'Traffic Cone',
            value: 'path',
            path: 'M44.62,43L32.721,8.504C31.585,5.21,28.484,3,25,3s-6.585,2.21-7.721,5.504L5.38,43H0v4h50v-4H44.62z M21.06,9.809 C21.64,8.129,23.223,7,25,7c1.777,0,3.36,1.129,3.939,2.809l2.136,6.193H18.924L21.06,9.809z M17.544,20.002h14.911l3.105,9.001 H14.439L17.544,20.002z M9.611,43l3.566-9.997h23.646L40.389,43H9.611z'
        },
        { name: 'SVG Path', value: 'path' }
    ];

    pointSizeOptions = ['2', '4', '6', '8', '10', '12', '14', '16', '18', '20', '22', '24', '30', '32', '36', '40', '50', '64', '72', '100', '128'];

    selectedOption: IPointStyleOption;

    CoordinateStyles: CoordinateStyle[] = ['None', 'Degrees', 'DMS'];
    selectedCoordsStyle: CoordinateStyle = 'Degrees';

    constructor(_vcr: ViewContainerRef,
                private readonly layerService: LayerService,
                private readonly cdr: ChangeDetectorRef) {
        super(_vcr);
    }

    colorPicker: ViewContainerRef;
    @ViewChild('colorPicker') set colorPickerRef(el: ViewContainerRef) { this.colorPicker = el; }
    outlineColorPicker: ViewContainerRef;
    @ViewChild('outlineColorPicker') set outlineColorPickerRef(el: ViewContainerRef) { this.outlineColorPicker = el; }

    async ngOnInit() {
        try {
            this.createLayerForEachGraphic = true;
            this.symbolProperties = this.defaultSymbolProperties;
            this.selectedOption = this.pointStyleOptions.find(opt => opt.name === this.defaultSymbolProperties.name && opt.value === this.defaultSymbolProperties.style);
            this.mapView = await this.setupMapViewRef(this.mapView);
            [Draw, Graphic, CoordinateFormatter, WebMercatorUtils] = await Promise.all([ESRI.Draw, ESRI.Graphic, ESRI.CoordinateFormatter, ESRI.WebMercatorUtils]);
            this.draw = new Draw({ view: this.mapView });
        } catch (err) {
            this.readyProblem(err);
        } finally {
            this.isReady();
        }
    }

    createPointDrawAction() {
        const action = this.draw.create('point');
        action.on('draw-complete', async (evt) => {
            const graphic = this.createPointGraphic(evt.vertices[0]);
            this.addPointGraphic(graphic);
            this.drawComplete.emit(graphic);
            this.createPointDrawAction();
        });
        this.cdr.detectChanges();
    }

    private createPointGraphic(coordinates) {
        const point = {
            type: 'point', // autocasts as Point
            x: coordinates[0],
            y: coordinates[1],
            spatialReference: this.mapView.spatialReference
        };

        const graphic = new Graphic({
            geometry: point,
            symbol: this.symbolProperties
        });

        return graphic;
    }

    private async addPointGraphic(graphic: __esri.Graphic) {
        if (!this.graphicsLayer || this.createLayerForEachGraphic) {
            let title = 'Point';
            if (this.selectedCoordsStyle !== 'None') {
                let point = graphic.geometry as __esri.Point;
                point = point.clone();
                if (this.selectedCoordsStyle === 'DMS') {
                    if (!CoordinateFormatter.isLoaded()) {
                        await CoordinateFormatter.load();
                    }
                    point = WebMercatorUtils.webMercatorToGeographic(point) as __esri.Point;
                    title += ' ' + CoordinateFormatter.toLatitudeLongitude(point, 'dms', maxLatLongDecimals);
                } else {
                    title += ` ${point.latitude}, ${point.longitude}`;
                }
            }
            this.graphicsLayer = await this.layerService.addGraphicsLayer({ title, graphicLayer: 'point' });
        }

        this.graphicsLayer.add(graphic);
        this.drawLayer.emit(this.graphicsLayer);
    }

    updateStyle(newVal: IPointStyleOption) {
        this.symbolProperties.style = newVal.value;
        this.symbolProperties.path = newVal.path;

        if (!this.symbolProperties.path) {
            delete this.symbolProperties.path;
        }
    }

    initializing = false;
    private readonly listners: IHandle[] = [];
    private setupHandleHiddenLayer() {
        this.initializing = true;
        const stopInitialCheck = util.debounce(() => { this.initializing = false; }, initilizeDebounceTime);
        this.listners.push(this.layerService.mapRef.allLayers.on('before-add', (event) => {
            if (this.initializing && event.item.listMode === 'hide') {
                event.item.set('hideInLegend', true);
                event.item.set('drawPointToolGeneratedLayer', true);
                stopInitialCheck();
            }
        }));
        this.listners.push(this.layerService.mapRef.allLayers.on('after-remove', (event) => {
            if (event.item.get<boolean>('drawPointToolGeneratedLayer')) {
                this.initializing = true;
            }
        }));
    }
    private teardownHandleHiddenLayer() {
        while (this.listners.length) {
            this.listners.pop().remove();
        }
    }
}

type Marker = 'circle' | 'square' | 'cross' | 'x' | 'diamond' | 'triangle' | 'path';
interface IPointStyleOption {
    name: string;
    value: Marker;
    path?: string;
}

type CoordinateStyle = 'Degrees' | 'DMS' | 'None';
